// QR Kiosk v2 — §8.3 history takeover parts: bucket header, thread row card,
// loading skeleton, empty state. Rows-as-cards per owner §2 preference. NO tag pill
// (every row is this machine → the desktop machine/platform pill is meaningless
// here). Tokens + glyphs only.
import React from "react";
import { Glyph, GD, Spark } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import type { ThreadListApiItem } from "../../../customer/midas/midasPageModels";

const hhmm = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function HistoryBucketHeader({ label }: { label: string }) {
  return (
    <div className="eyebrow" style={{ margin: "14px 2px 8px" }}>
      {label}
    </div>
  );
}

export function HistoryRow({
  item,
  active,
  opening,
  msgsLabel,
  onOpen,
}: {
  item: ThreadListApiItem;
  active: boolean;
  opening: boolean;
  msgsLabel: string;
  onOpen: () => void;
}) {
  const press = usePressed();
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={onOpen}
      disabled={opening}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 12,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderLeft: active ? "3px solid var(--midas)" : "1px solid var(--border)",
        boxShadow: "var(--shadow)",
        marginBottom: 8,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        opacity: opening ? 0.6 : 1,
        ...press.pressStyle,
      }}
    >
      <div style={{ width: 52, flexShrink: 0, fontFamily: "var(--font-mono)" }}>
        <div style={{ fontSize: 13, color: "var(--ink-2)" }}>{hhmm(item.updated_at)}</div>
        <div style={{ fontSize: 10.5, color: "var(--faint)", letterSpacing: "0.04em" }}>
          {`${item.message_count} ${msgsLabel}`}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "var(--ink)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.title}
        </div>
        {item.last_message_preview && (
          <div
            style={{
              fontSize: 13,
              color: "var(--muted)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginTop: 2,
            }}
          >
            {item.last_message_preview}
          </div>
        )}
      </div>
      <Glyph d={GD.chev} size={16} style={{ color: "var(--faint)", flexShrink: 0 }} />
    </button>
  );
}

export function HistorySkeleton() {
  return (
    <div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="kv2-sk" style={{ height: 64, borderRadius: 12, marginBottom: 8 }} />
      ))}
    </div>
  );
}

export function HistoryEmpty({ text }: { text: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: "48px 16px",
        textAlign: "center",
      }}
    >
      <span style={{ color: "var(--midas)", display: "inline-flex" }}>
        <Spark size={26} />
      </span>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink-2)" }}>{text}</div>
    </div>
  );
}
